import { landing } from "../landingTokens";
import { generateUuidV4, getForwardedTrackingParams, trackLandingCallToActionClick, trackLandingEvent } from "./appHandoff";

/**
 * The app store calls to action on the marketing pages, shown only when a store route is forced for QA.
 *
 * Until 2026-10-12 phone visitors who arrived from a Meta ad were offered the iOS / Android app instead of the web
 * sign-up. The owner chose the web upload page for them after the store-versus-web test, and since then they get the
 * same page as everyone else: no store route, and no redirect (the redirect that used to do this ran on real
 * phones but not for Meta's crawler, the pattern Meta's ad review treats as cloaking).
 *
 * Whether a page is routed is decided before the first paint by the inline `<script data-app-store-route-script>` in
 * marketing.html, which marks `<html data-app-store-route="ios|android">`. `resolveAppStoreRoute` below is
 * the same rule as a pure function: appStoreRoute.test.ts evaluates the inline script against it, so the two
 * cannot drift apart unnoticed. Pages render the web and the store calls to action for every visitor, with
 * the same markup at build time and in the browser, and marketingContent.css shows the ones that apply; a
 * render must never branch on the route, or React would discard the static page. Nobody is redirected: the
 * visitor taps a store link, or "Continue in browser" to get the web flow back.
 *
 * The rules (the inline script is compact because it ships in every page's HTML; change both together):
 * - `?app-store-route=ios|android` forces a route for one page load (QA, any device); `?app-store-route=off`
 *   forgets the values the retired Meta ad route stored (`app_store_route_until`, `app_store_route_declined`);
 *   nothing reads them any more;
 * - "Continue in browser" on a routed page leaves it. The inline script also handles its click (a document
 *   listener), so the way back works before React has hydrated and when the page's JavaScript fails to load. After
 *   hydration continueInBrowser runs first and the listener sees the route already gone; a click the listener
 *   handled itself is left in `window.jungleContinuedInBrowserBeforeHydration` (the store) for
 *   reportContinueInBrowserChosenBeforeHydration;
 * - a Play link tapped before hydration still has its static href, without `referrer`; the same listener then
 *   appends the referrer buildGooglePlayLink would build from this URL's campaign (same keys, order, 200-character
 *   cap and email rule) to the href, before the browser follows it, so an early tap keeps the Android install
 *   attribution and a new-tab click still opens a new tab. The page usually unloads at once; if it stays (new tab,
 *   Play app), React 18 keeps the changed attribute, and its post-hydration href is the same. It reads only the
 *   URL: when the URL carries a campaign that is exactly getForwardedTrackingParams' result; without one the tap
 *   opens the plain link as before (the hydrated page could still add a campaign remembered from an earlier visit;
 *   the script leaves that out to stay within the page-size budget). appStoreRoutePlayReferrer.test.ts runs the
 *   script against buildAppStoreUrl;
 * - every storage access has its own try/catch (private mode, blocked site data).
 */

/**
 * A store, as the page uses it (the <html> attribute, the query override, the CSS classes) and as analytics
 * reports it: the `store` property of "Landing Page: Call To Action Clicked", the `appStoreRoute` property of
 * "Landing Page Viewed" and the Meta pixel's AppStoreClick. The backend's OpenPanel event catalog
 * (docs/analytics/openpanel/event-catalog.json) registers these same values.
 */
export const APP_STORES = ["ios", "android"] as const;
export type AppStore = (typeof APP_STORES)[number];

/** Where on the page a store link sits; reported with every click. */
export type AppStorePlacement = "hero" | "nav" | "nav_menu" | "closing_cta" | "footer" | "section_badges";

export const APP_STORE_ROUTE_HTML_ATTRIBUTE = "data-app-store-route";
export const APP_STORE_ROUTE_SCRIPT_ATTRIBUTE = "data-app-store-route-script";
// `?app-store-route=ios|android` forces a route for one page load; `off` shows the web flow and forgets both
// stored values below. For QA on any device.
export const APP_STORE_ROUTE_QUERY_PARAM = "app-store-route";
// Written by the retired Meta ad route (a visit remembered for 7 days) and by "Continue in browser"; nothing reads
// them any more, and `?app-store-route=off` removes them.
export const APP_STORE_ROUTE_UNTIL_STORAGE_KEY = "app_store_route_until";
export const APP_STORE_ROUTE_DECLINED_STORAGE_KEY = "app_store_route_declined";
// The "Continue in browser" control; the inline script's click listener finds it by this class.
export const CONTINUE_IN_BROWSER_CLASS_NAME = "app-store-continue-in-browser";
// Where the inline script leaves the store of a "Continue in browser" click it handled before hydration.
export const CONTINUED_IN_BROWSER_BEFORE_HYDRATION_WINDOW_KEY = "jungleContinuedInBrowserBeforeHydration";
const DECLINED_STORAGE_VALUE = "1";

/**
 * The App Store Connect provider token (App Analytics → Campaigns: the `pt` value of a campaign link). With it
 * the App Store link is a campaign link whose campaign token (`ct`) names the ad, so App Analytics counts App
 * Store page views, installs and proceeds per ad (an empty token would give the plain link). That is aggregate
 * only: iOS gets no per-user install attribution (Meta attributes app sign-ups and purchases through the
 * Conversions API). Android attribution is per user, through the Google Play install referrer.
 */
export const APP_STORE_PROVIDER_TOKEN: string = "125369198";
// The campaign token when there is no ad to name, and in the static HTML before hydration.
export const DEFAULT_APP_STORE_CAMPAIGN_TOKEN = "Fall26";

type AppStoreRouteStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function writeStorage(storage: AppStoreRouteStorage | null, key: string, value: string | null): void {
  try {
    if (!storage) return;
    if (value === null) storage.removeItem(key);
    else storage.setItem(key, value);
  } catch {
    // Storage can be unavailable (private mode); the route then only follows the current URL.
  }
}

/**
 * The route the inline script in marketing.html assigns, with the same storage writes: a store only when QA forces
 * one. `storage` may be null or throw on every call (private mode, blocked site data).
 */
export function resolveAppStoreRoute({ search, storage }: { search: string; storage: AppStoreRouteStorage | null }): AppStore | null {
  const searchParams = new URLSearchParams(search);
  const override = searchParams.get(APP_STORE_ROUTE_QUERY_PARAM);
  if (override === "ios" || override === "android") return override;
  if (override === "off") {
    writeStorage(storage, APP_STORE_ROUTE_UNTIL_STORAGE_KEY, null);
    writeStorage(storage, APP_STORE_ROUTE_DECLINED_STORAGE_KEY, null);
  }
  return null;
}

/** The route the inline script put on this page and that is still in force, for click handlers (never for rendering). */
export function readAppStoreRouteFromDocument(): AppStore | null {
  if (typeof document === "undefined") return null;
  const route = document.documentElement.getAttribute(APP_STORE_ROUTE_HTML_ATTRIBUTE);
  return route === "ios" || route === "android" ? route : null;
}

function readStoreContinuedInBrowserBeforeHydration(): AppStore | null {
  if (typeof window === "undefined") return null;
  const store = (window as any)[CONTINUED_IN_BROWSER_BEFORE_HYDRATION_WINDOW_KEY];
  return store === "ios" || store === "android" ? store : null;
}

/**
 * "Landing Page Viewed"'s `appStoreRoute`: the store this phone visitor from a Meta ad was offered, else null,
 * including a visitor who already chose "Continue in browser" before the page hydrated.
 */
export function readAppStoreRouteOfferedOnThisPage(): AppStore | null {
  return readAppStoreRouteFromDocument() ?? readStoreContinuedInBrowserBeforeHydration();
}

/**
 * Reports a "Continue in browser" click that the inline script handled before hydration, once. Call it right
 * after the page's view has been reported, so the choice is never sent before the view. Only the hero's control
 * is in the static page (AppStoreCallToAction.tsx), so that is the placement.
 */
export function reportContinueInBrowserChosenBeforeHydration(): void {
  const store = readStoreContinuedInBrowserBeforeHydration();
  if (typeof window !== "undefined") delete (window as any)[CONTINUED_IN_BROWSER_BEFORE_HYDRATION_WINDOW_KEY];
  if (!store) return;
  trackLandingEvent("Landing Page: Call To Action Clicked", { callToAction: "continue_in_browser", store, placement: "hero" });
}

// What Google Play receives as the install referrer: the campaign (utm_*, including Meta's utm_id) and Meta ad
// ids (fb_*) that getForwardedTrackingParams forwards to the app on a web handoff (FORWARDED_ATTRIBUTION_PARAMS
// in appHandoff.ts lists them). Click ids and referral codes are not sent to the stores. marketing.html's inline
// route script repeats these keys and the value rules below for Play taps before hydration: change both together.
const STORE_REFERRER_PARAM_PATTERN = /^(utm_|fb_)/;

// Values leave for a third party (Google Play, Apple), so they get the attribution rules of the shared
// contract (C2): at most 200 characters, and nothing that looks like an email address (an email campaign's
// utm_content can carry one). The same rule as sanitizeAttributionValue in utils/acquisition-attribution.ts
// on the web-tracking branch; reuse that once both have merged.
const MAX_STORE_ATTRIBUTION_VALUE_LENGTH = 200;
const EMAIL_ADDRESS_PATTERN = /[^\s@]+@[^\s@]+\.[^\s@]+/;

export function sanitizeStoreAttributionValue(value: string | null): string | null {
  const trimmedValue = (value ?? "").trim();
  if (!trimmedValue || EMAIL_ADDRESS_PATTERN.test(trimmedValue)) return null;
  return trimmedValue.slice(0, MAX_STORE_ATTRIBUTION_VALUE_LENGTH);
}

// App Store Connect accepts campaign tokens of up to 40 characters.
const MAX_APP_STORE_CAMPAIGN_TOKEN_LENGTH = 40;
const APP_STORE_CAMPAIGN_URL = "https://apps.apple.com/app/apple-store/id6657995165";

/** The App Store campaign token (`ct`): the Meta ad id, else utm_content, at most 40 characters, else the default. */
export function buildAppStoreCampaignToken(forwardedParams: URLSearchParams): string {
  const campaignToken =
    sanitizeStoreAttributionValue(forwardedParams.get("fb_ad_id")) ??
    sanitizeStoreAttributionValue(forwardedParams.get("utm_content"));
  // By code point, so an emoji is never cut in half (encodeURIComponent throws on a lone surrogate).
  return campaignToken
    ? Array.from(campaignToken).slice(0, MAX_APP_STORE_CAMPAIGN_TOKEN_LENGTH).join("")
    : DEFAULT_APP_STORE_CAMPAIGN_TOKEN;
}

function buildAppStoreLink(providerToken: string, forwardedParams: URLSearchParams): string {
  // Without a provider token there is nothing to attribute: the plain link.
  if (!providerToken) return landing.appStoreUrl;
  const campaignToken = buildAppStoreCampaignToken(forwardedParams);
  return `${APP_STORE_CAMPAIGN_URL}?pt=${encodeURIComponent(providerToken)}&ct=${encodeURIComponent(campaignToken)}&mt=8`;
}

function buildGooglePlayLink(forwardedParams: URLSearchParams): string {
  const referrerParams = new URLSearchParams();
  for (const [paramName, paramValue] of forwardedParams) {
    if (!STORE_REFERRER_PARAM_PATTERN.test(paramName)) continue;
    const value = sanitizeStoreAttributionValue(paramValue);
    if (value) referrerParams.set(paramName, value);
  }
  const referrer = referrerParams.toString();
  return referrer ? `${landing.googlePlayUrl}&referrer=${encodeURIComponent(referrer)}` : landing.googlePlayUrl;
}

/**
 * A store link. `withVisitorParams: false` leaves out everything read from this browser, which is what an
 * href must say in the build-time rendering and in React's first render (see useAppStoreUrl in useAppUrl.ts).
 * Google Play carries the attribution in `referrer` (the Play install referrer the app reads on first launch);
 * the App Store link is an App Store Connect campaign link (the default campaign token until hydrated).
 */
export function buildAppStoreUrl(
  store: AppStore,
  {
    withVisitorParams = true,
    appStoreProviderToken = APP_STORE_PROVIDER_TOKEN,
  }: { withVisitorParams?: boolean; appStoreProviderToken?: string } = {}
): string {
  const forwardedParams = withVisitorParams ? getForwardedTrackingParams() : new URLSearchParams();
  if (store === "ios") return buildAppStoreLink(appStoreProviderToken.trim(), forwardedParams);
  return buildGooglePlayLink(forwardedParams);
}

/**
 * A tap on a store link: the landing CTA event, and a custom Meta pixel event, the only web signal Meta gets
 * for a visitor who then signs up inside the app. `store` is reported as ios / android.
 */
export function trackAppStoreClick(store: AppStore, placement: AppStorePlacement): void {
  const storeClickProperties = { store, placement };
  trackLandingCallToActionClick("get_app", storeClickProperties);
  const fbq = typeof window !== "undefined" ? (window as any).fbq : undefined;
  if (typeof fbq !== "function") return;
  try {
    fbq("trackCustom", "AppStoreClick", storeClickProperties, { eventID: generateUuidV4() });
  } catch {
    // Ad blockers can stub fbq out; the store link must still open.
  }
}

/** "Continue in browser": the web calls to action come back at once and this browser is not routed again. */
export function continueInBrowser(placement: AppStorePlacement): void {
  // The store the page offered, read before the attribute that records it is removed.
  const store = readAppStoreRouteFromDocument();
  document.documentElement.removeAttribute(APP_STORE_ROUTE_HTML_ATTRIBUTE);
  let storage: Storage | null = null;
  try {
    storage = window.localStorage;
  } catch {
    storage = null;
  }
  writeStorage(storage, APP_STORE_ROUTE_DECLINED_STORAGE_KEY, DECLINED_STORAGE_VALUE);
  // The landing CTA event, as the backend's event catalog registers it (callToAction continue_in_browser, store,
  // placement). Sent through trackLandingEvent, not trackLandingCallToActionClick: choosing the web flow is not
  // a call to action into the app, so it fires no Google Ads or TikTok conversion.
  trackLandingEvent("Landing Page: Call To Action Clicked", { callToAction: "continue_in_browser", store, placement });
}
